import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Tabs,
    Tab,
    Accordion,
    Alert,
} from 'react-bootstrap';
import AccessibleProgressBar from '@/components/common/AccessibleProgressBar';
import trainingService from '@/shared/services/trainingService';
import { useTerm } from '@/shared/contexts/TermContext';
import '@/features/training/TrainingPage.css';
import { badgeTextFor } from '@/shared/utils/badgeText';
import LoadingState from '@/components/common/LoadingState';
import LoadError from '@/components/common/LoadError';

const PROGRAM_CONFIG = {
    si: {
        bg: 'success',
        label: 'SI',
        full: 'Supplemental Instruction',
        color: '#2E7D32',
    },
    ta: {
        bg: 'primary',
        label: 'TA',
        full: 'Teaching Assistant',
        color: '#1565C0',
    },
    la: {
        bg: '',
        label: 'LA',
        full: 'Learning Assistant',
        color: 'var(--psh-la-color)',
        badgeClass: 'badge-la',
    },
    all: {
        bg: 'secondary',
        label: 'All',
        full: 'All Programs',
        color: '#6c757d',
    },
};

const MODULE_STATUS_CONFIG = {
    completed: { bg: 'success', label: 'Completed', icon: '✓' },
    in_progress: { bg: 'warning', label: 'In Progress', icon: '◐' },
    not_started: { bg: 'secondary', label: 'Not Started', icon: '○' },
};

const MODULE_TYPE_CONFIG = {
    video: { icon: '🎬', label: 'Video' },
    document: { icon: '📄', label: 'Document' },
    quiz: { icon: '📝', label: 'Quiz' },
    workshop: { icon: '🎓', label: 'Workshop' },
    external: { icon: '🔗', label: 'External' },
};

function TrainingPage() {
    const [trainingData, setTrainingData] = useState(null);
    const [modules, setModules] = useState([]);
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [activeTab, setActiveTab] = useState('modules');
    const [actionLoading, setActionLoading] = useState(null);
    const { currentTermCode, termsLoading } = useTerm();

    useEffect(() => {
        if (termsLoading) return;
        const loadTrainingData = async () => {
            try {
                setLoading(true);
                setError(null);
                setLoadFailed(false);

                const [myTraining, allModules, myCertificates] =
                    await Promise.all([
                        trainingService.getMyTraining({
                            term: currentTermCode,
                        }),
                        trainingService.getModules(),
                        trainingService.getMyCertificates(),
                    ]);

                setTrainingData(myTraining);
                setModules(allModules);
                setCertificates(myCertificates);
            } catch (_err) {
                setLoadFailed(true);
            } finally {
                setLoading(false);
            }
        };

        loadTrainingData();
    }, [termsLoading, currentTermCode, reloadKey]);

    const reloadTrainingData = async () => {
        const [myTraining, allModules, myCertificates] = await Promise.all([
            trainingService.getMyTraining({ term: currentTermCode }),
            trainingService.getModules(),
            trainingService.getMyCertificates(),
        ]);
        setTrainingData(myTraining);
        setModules(allModules);
        setCertificates(myCertificates);
    };

    const handleStartModule = async (module) => {
        try {
            setActionLoading(module._id);
            setError(null);

            const programType = module.programTypes?.[0] || 'all';
            await trainingService.updateProgress({
                moduleId: module._id,
                programType,
                status: 'in_progress',
                progress: 0,
            });

            await reloadTrainingData();
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to start module. Please try again.';
            setError(message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleContinueModule = async (module) => {
        const record = getRecordForModule(module._id);
        const currentProgress = record?.progress || 0;

        // If the module has a URL resource, open it
        const resourceUrl = module.resources?.find((r) => r.url)?.url;
        if (resourceUrl) {
            window.open(resourceUrl, '_blank', 'noopener,noreferrer');
        }

        // Increment progress by 25% per interaction (capped at 75% - completion is separate)
        const newProgress = Math.min(currentProgress + 25, 75);
        try {
            setActionLoading(module._id);
            setError(null);

            const programType = module.programTypes?.[0] || 'all';
            await trainingService.updateProgress({
                moduleId: module._id,
                programType,
                status: 'in_progress',
                progress: newProgress,
            });

            await reloadTrainingData();
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to update progress. Please try again.';
            setError(message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleReviewModule = (module) => {
        // Open the module resource for review
        const resourceUrl = module.resources?.find((r) => r.url)?.url;
        if (resourceUrl) {
            window.open(resourceUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleDownloadCertificate = async (cert) => {
        try {
            setActionLoading(cert._id);
            setError(null);

            // Get the module detail which may contain certificate info
            const moduleData = await trainingService.getModuleById(
                cert.module?._id || cert.module
            );

            // If a certificate URL is available from the module or record, open it
            const certUrl =
                moduleData?.module?.certificateUrl ||
                moduleData?.userProgress?.certificateUrl;
            if (certUrl) {
                window.open(certUrl, '_blank', 'noopener,noreferrer');
            } else {
                // Fallback: notify user that download will be available
                setError(
                    'Certificate download is being prepared. Please try again shortly.'
                );
            }
        } catch (err) {
            const message =
                err.response?.data?.message ||
                'Failed to download certificate. Please try again.';
            setError(message);
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const getOverallProgress = () => {
        if (!trainingData?.summary) return 0;
        return trainingData.summary.overallProgress || 0;
    };

    const getRecordForModule = (moduleId) => {
        return trainingData?.records?.find(
            (r) => r.module?._id === moduleId || r.module === moduleId
        );
    };

    const renderModuleCard = (module) => {
        const record = getRecordForModule(module._id);
        const status = record?.status || 'not_started';
        const statusConfig = MODULE_STATUS_CONFIG[status];
        const typeConfig =
            MODULE_TYPE_CONFIG[module.resources?.[0]?.type] ||
            MODULE_TYPE_CONFIG.video;

        return (
            <Card key={module._id} className="mb-3 border-0 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col xs="auto">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor:
                                        status === 'completed'
                                            ? '#d4edda'
                                            : '#f8f9fa',
                                    fontSize: '1.5rem',
                                }}
                            >
                                {status === 'completed' ? '✓' : typeConfig.icon}
                            </div>
                        </Col>
                        <Col>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <h3 className="h6 mb-0">{module.name}</h3>
                                <Badge
                                    bg={statusConfig.bg}
                                    text={badgeTextFor(statusConfig.bg)}
                                    className="small"
                                >
                                    {statusConfig.label}
                                </Badge>
                            </div>
                            <p className="text-muted small mb-1">
                                {module.description}
                            </p>
                            <div className="d-flex align-items-center gap-3 small">
                                <span className="text-muted">
                                    {typeConfig.icon} {typeConfig.label}
                                </span>
                                {module.estimatedMinutes && (
                                    <span className="text-muted">
                                        ⏱️ {module.estimatedMinutes} min
                                    </span>
                                )}
                                {record?.completedAt && (
                                    <span className="text-success">
                                        Completed{' '}
                                        {formatDate(record.completedAt)}
                                    </span>
                                )}
                            </div>
                            {status === 'in_progress' &&
                                record?.progress > 0 && (
                                    <div className="mt-2">
                                        <AccessibleProgressBar
                                            now={record.progress}
                                            variant="warning"
                                            className="training-module-progress"
                                            label={`Module ${record.progress}% complete`}
                                        />
                                        <small className="text-muted">
                                            {record.progress}% complete
                                        </small>
                                    </div>
                                )}
                        </Col>
                        <Col xs="auto">
                            {status === 'completed' ? (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    disabled={actionLoading === module._id}
                                    onClick={() => handleReviewModule(module)}
                                >
                                    {actionLoading === module._id
                                        ? 'Loading...'
                                        : 'Review'}
                                </Button>
                            ) : status === 'in_progress' ? (
                                <Button
                                    variant="warning"
                                    size="sm"
                                    disabled={actionLoading === module._id}
                                    onClick={() => handleContinueModule(module)}
                                >
                                    {actionLoading === module._id
                                        ? 'Loading...'
                                        : 'Continue'}
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    disabled={actionLoading === module._id}
                                    onClick={() => handleStartModule(module)}
                                >
                                    {actionLoading === module._id
                                        ? 'Starting...'
                                        : 'Start'}
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    if (loading) {
        return (
            <Container className="py-4">
                <h1>Training</h1>
                <LoadingState rows={3} label="Loading training data" />
            </Container>
        );
    }

    if (loadFailed) {
        return (
            <Container className="py-4">
                <h1>Training</h1>
                <LoadError
                    what="your training"
                    onRetry={() => setReloadKey((k) => k + 1)}
                />
            </Container>
        );
    }

    // Group modules by program type
    const modulesByProgram = modules.reduce((acc, module) => {
        const programs = module.programTypes || ['all'];
        programs.forEach((program) => {
            if (!acc[program]) {
                acc[program] = [];
            }
            acc[program].push(module);
        });
        return acc;
    }, {});

    const summary = trainingData?.summary || {
        completed: 0,
        total: 0,
        overallProgress: 0,
    };

    const { trainingDeadline, daysRemaining } = summary;
    const isOverdue = trainingDeadline && daysRemaining < 0;
    const overdueDays = Math.abs(daysRemaining);
    const deadlineVariant = isOverdue
        ? 'danger'
        : daysRemaining <= 7
          ? 'warning'
          : 'info';

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                    <h1>Training</h1>
                    <p className="text-muted">
                        Complete required training and access resources for your
                        programs
                    </p>
                </div>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Progress Overview */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={8}>
                            <h2 className="h5 mb-3">Your Training Progress</h2>
                            <div className="mb-2">
                                <div className="d-flex justify-content-between mb-1">
                                    <span>
                                        {summary.completed} of {summary.total}{' '}
                                        modules completed
                                    </span>
                                    <span className="fw-bold">
                                        {getOverallProgress()}%
                                    </span>
                                </div>
                                <AccessibleProgressBar
                                    now={getOverallProgress()}
                                    variant="success"
                                    label={`Training progress: ${getOverallProgress()}%`}
                                />
                            </div>
                            <div className="d-flex gap-4 mt-3">
                                <div>
                                    <Badge bg="success" className="me-1">
                                        {summary.completed}
                                    </Badge>
                                    <span className="small text-muted">
                                        Completed
                                    </span>
                                </div>
                                <div>
                                    <Badge bg="secondary" className="me-1">
                                        {summary.total - summary.completed}
                                    </Badge>
                                    <span className="small text-muted">
                                        Remaining
                                    </span>
                                </div>
                            </div>
                            {trainingDeadline && (
                                <Alert
                                    variant={deadlineVariant}
                                    className="mt-3 mb-0 py-2"
                                >
                                    {isOverdue ? (
                                        <>
                                            <strong>Training overdue:</strong>{' '}
                                            deadline was{' '}
                                            {formatDate(trainingDeadline)} (
                                            {overdueDays}{' '}
                                            {overdueDays === 1 ? 'day' : 'days'}{' '}
                                            ago).
                                        </>
                                    ) : daysRemaining === 0 ? (
                                        <>
                                            <strong>Training due today:</strong>{' '}
                                            {formatDate(trainingDeadline)}.
                                        </>
                                    ) : (
                                        <>
                                            <strong>Training due</strong>{' '}
                                            {formatDate(trainingDeadline)} —{' '}
                                            {daysRemaining}{' '}
                                            {daysRemaining === 1
                                                ? 'day'
                                                : 'days'}{' '}
                                            remaining.
                                        </>
                                    )}
                                </Alert>
                            )}
                        </Col>
                        <Col md={4} className="text-center border-start">
                            <div className="display-4 text-primary mb-1">
                                {certificates.length}
                            </div>
                            <p className="text-muted mb-0">
                                certificates earned
                            </p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Main Content Tabs */}
            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4"
            >
                {/* Training Modules Tab */}
                <Tab eventKey="modules" title="Training Modules">
                    <Card className="border-0 bg-light mb-3">
                        <Card.Body className="py-2">
                            <small className="text-muted">
                                Complete all required modules for your assigned
                                programs. Click Start to begin a module, or
                                Continue to resume where you left off.
                            </small>
                        </Card.Body>
                    </Card>

                    {Object.keys(modulesByProgram).length === 0 ? (
                        <Card className="border-0 bg-light">
                            <Card.Body className="text-center py-5">
                                <div className="display-1 mb-3">📚</div>
                                <h2 className="h5">
                                    No Training Modules Assigned
                                </h2>
                                <p className="text-muted mb-0">
                                    Training modules will appear here once you
                                    have an active assignment.
                                </p>
                            </Card.Body>
                        </Card>
                    ) : (
                        Object.entries(modulesByProgram).map(
                            ([programType, programModules]) => {
                                const programConfig =
                                    PROGRAM_CONFIG[programType] ||
                                    PROGRAM_CONFIG.all;
                                const completedCount = programModules.filter(
                                    (m) => {
                                        const record = getRecordForModule(
                                            m._id
                                        );
                                        return record?.status === 'completed';
                                    }
                                ).length;

                                return (
                                    <div key={programType} className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge
                                                    bg={programConfig.bg}
                                                    text={badgeTextFor(
                                                        programConfig.bg
                                                    )}
                                                    className={
                                                        programConfig.badgeClass ||
                                                        ''
                                                    }
                                                >
                                                    {programConfig.label}
                                                </Badge>
                                                <h2 className="h5 mb-0">
                                                    {programConfig.full}{' '}
                                                    Training
                                                </h2>
                                            </div>
                                            <span className="text-muted small">
                                                {completedCount} /{' '}
                                                {programModules.length}{' '}
                                                completed
                                            </span>
                                        </div>
                                        {programModules.map((module) =>
                                            renderModuleCard(module)
                                        )}
                                    </div>
                                );
                            }
                        )
                    )}
                </Tab>

                {/* Resources Tab */}
                <Tab eventKey="resources" title="Resources">
                    <Row>
                        {modules
                            .filter((m) => m.resources?.length > 0)
                            .map((module) => {
                                const programType =
                                    module.programTypes?.[0] || 'all';
                                const programConfig =
                                    PROGRAM_CONFIG[programType] ||
                                    PROGRAM_CONFIG.all;
                                return module.resources.map((resource, idx) => (
                                    <Col
                                        md={6}
                                        key={`${module._id}-${idx}`}
                                        className="mb-3"
                                    >
                                        <Card className="h-100 border-0 shadow-sm">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <Badge
                                                        bg={programConfig.bg}
                                                        text={badgeTextFor(
                                                            programConfig.bg
                                                        )}
                                                        className={
                                                            programConfig.badgeClass ||
                                                            ''
                                                        }
                                                    >
                                                        {programConfig.label}
                                                    </Badge>
                                                    <Badge
                                                        bg="light"
                                                        text="dark"
                                                    >
                                                        {resource.type?.toUpperCase() ||
                                                            'RESOURCE'}
                                                    </Badge>
                                                </div>
                                                <h3 className="h6">
                                                    {resource.title ||
                                                        module.name}
                                                </h3>
                                                <p className="text-muted small mb-3">
                                                    {resource.description ||
                                                        module.description}
                                                </p>
                                                {resource.url ? (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        as="a"
                                                        href={resource.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        View Resource
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        disabled
                                                    >
                                                        Coming Soon
                                                    </Button>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ));
                            })}
                    </Row>
                    {modules.every((m) => !m.resources?.length) && (
                        <Card className="border-0 bg-light">
                            <Card.Body className="text-center py-5">
                                <p className="text-muted mb-0">
                                    No resources available yet. Check back later
                                    for training materials.
                                </p>
                            </Card.Body>
                        </Card>
                    )}
                </Tab>

                {/* Certificates Tab */}
                <Tab
                    eventKey="certificates"
                    title={`Certificates (${certificates.length})`}
                >
                    {certificates.length === 0 ? (
                        <Card className="border-0 bg-light">
                            <Card.Body className="text-center py-5">
                                <div className="display-1 mb-3">🎓</div>
                                <h2 className="h5">No Certificates Yet</h2>
                                <p className="text-muted mb-0">
                                    Complete your training modules to earn
                                    certificates.
                                </p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Row>
                            {certificates.map((cert) => {
                                const programConfig =
                                    PROGRAM_CONFIG[cert.programType] ||
                                    PROGRAM_CONFIG.all;
                                return (
                                    <Col
                                        md={6}
                                        lg={4}
                                        key={cert._id}
                                        className="mb-3"
                                    >
                                        <Card className="border-0 shadow-sm text-center">
                                            <Card.Body className="py-4">
                                                <div className="display-4 mb-2">
                                                    🏆
                                                </div>
                                                <Badge
                                                    bg={programConfig.bg}
                                                    text={badgeTextFor(
                                                        programConfig.bg
                                                    )}
                                                    className={`mb-2 ${programConfig.badgeClass || ''}`}
                                                >
                                                    {programConfig.label}
                                                </Badge>
                                                <h3 className="h6">
                                                    {cert.module?.name ||
                                                        'Training Certificate'}
                                                </h3>
                                                <small className="text-muted">
                                                    Issued{' '}
                                                    {formatDate(
                                                        cert.certificateIssuedAt ||
                                                            cert.completedAt
                                                    )}
                                                </small>
                                                <div className="mt-3">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        disabled={
                                                            actionLoading ===
                                                            cert._id
                                                        }
                                                        onClick={() =>
                                                            handleDownloadCertificate(
                                                                cert
                                                            )
                                                        }
                                                    >
                                                        {actionLoading ===
                                                        cert._id
                                                            ? 'Loading...'
                                                            : 'Download Certificate'}
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                    <Card className="border-0 bg-light mt-3">
                        <Card.Body className="text-center py-4">
                            <p className="text-muted mb-0">
                                Click &ldquo;Download Certificate&rdquo; to
                                retrieve your completion certificate.
                            </p>
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>

            {/* Help Section */}
            <Card className="mt-4 bg-light border-0">
                <Card.Body>
                    <h2 className="h5 fw-bold mb-3">Training FAQ</h2>
                    <Accordion flush>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                What training is required?
                            </Accordion.Header>
                            <Accordion.Body className="small">
                                <p>
                                    Required training depends on your program
                                    assignment:
                                </p>
                                <ul>
                                    <li>
                                        <strong>SI Leaders:</strong> Complete
                                        all SI Leader Training modules before
                                        your first session.
                                    </li>
                                    <li>
                                        <strong>Teaching Assistants:</strong>{' '}
                                        Training requirements vary by
                                        department. Check with your faculty
                                        member.
                                    </li>
                                    <li>
                                        <strong>Learning Assistants:</strong>{' '}
                                        Complete all LA Pedagogy Training
                                        modules, typically during your first two
                                        weeks.
                                    </li>
                                </ul>
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                How long does training take?
                            </Accordion.Header>
                            <Accordion.Body className="small">
                                Most training programs require 4-6 hours of
                                total training time, which can be completed at
                                your own pace. Interactive modules may require
                                additional time for practice activities.
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="2">
                            <Accordion.Header>
                                When will the interactive modules be available?
                            </Accordion.Header>
                            <Accordion.Body className="small">
                                Interactive training modules are currently under
                                development. You&apos;ll be notified when they
                                become available. In the meantime, you can
                                review the video-based modules and download
                                available resources.
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="3">
                            <Accordion.Header>
                                How do I get my certificate?
                            </Accordion.Header>
                            <Accordion.Body className="small">
                                Certificates are automatically issued when you
                                complete all required modules for a training
                                program. You can download your certificates from
                                the Certificates tab above.
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default TrainingPage;
